import { useExperience, useNinetailed } from '@ninetailed/experience.js-next';
import { ExperienceMapper } from '@ninetailed/experience.js-utils';
import { useExperienceTrackingContext } from 'contexts/experience-tracking';
import { useNinetailedAbTestContext, type TestConfig } from 'contexts/ninetailed-ab-test';
import { GetAbTestByIdQuery } from 'generated/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';
import { useAbTestCacheStore } from 'stores/ab-test-cache';
import { isVariantRef } from 'utils/ninetailed';
import { omitNullableHandler } from 'utils/type-helper';
import useIsClient from './use-is-client';

type ProfileState = any;

interface Input {
  /** Id of base entry attached to Experiment config */
  baseEntryId: string;
  /** if true, resolved variant is set to header to communicate with BE */
  shouldAddToHeader?: boolean;
  /**
   * Ref Object to element used for tracking purpose.
   * If A/B test is associated with a component, this should be specified
   * */
  elementRef?: React.RefObject<Element>;
  /**
   * Callback when a user becomes eligible to a/b test or the other way around.
   * Useful when we have to recalculate based on test variant.
   * e.g discount for NC but not for EC.
   */
  onExperimentChange?: (profile?: ProfileState) => void | Promise<void>;
}

export interface Output {
  baseUrl?: string | null;
  experienceId?: string;
  experienceName?: string;
  loading: boolean;
  redirectUrl?: string | null;
  status: 'loading' | 'success' | 'error';
  testData: unknown | undefined;
  variant: number | undefined;
  /** Trigger tracking for Ninetailed experience. */
  trackComponentViewFn: () => void;
}

type DeepRequired<T> = {
  [P in keyof T]-?: NonNullable<T[P]> extends object
    ? DeepRequired<NonNullable<T[P]>>
    : NonNullable<T[P]>;
};

type RequiredGetAbTestByIdQuery = DeepRequired<GetAbTestByIdQuery>;

/**
 * @TODO : create cache logic
 */
const useExperiment = ({
  baseEntryId,
  shouldAddToHeader,
  elementRef,
  onExperimentChange,
}: Input): Output => {
  const { abTestCacheStore, setAbTestCacheStore } = useAbTestCacheStore();
  const { isClient } = useIsClient();

  const { setToHeader, getTestEntry, isLoading, deleteHeader } = useNinetailedAbTestContext();
  const [exVariant, setExVariant] = useState<number | undefined>(
    abTestCacheStore[baseEntryId]?.variant
  );

  const [testData, setTestData] = useState<unknown | undefined>(
    abTestCacheStore[baseEntryId]?.testData
  );

  const entryPromise = getTestEntry(baseEntryId);

  const [baseLine, setBaseline] = useState<TestConfig | undefined>();
  const [isFetched, setIsFetched] = useState<boolean>(false);

  useEffect(() => {
    if (!isLoading && !isFetched) {
      setBaseline(getTestEntry(baseEntryId));
      setIsFetched(true);
    }
  }, [baseEntryId, entryPromise, getTestEntry, isFetched, isLoading]);

  const _baseline = useMemo(() => {
    if (!baseLine) return { id: '', testData: undefined, baseUrl: null, redirectUrl: null };
    return {
      id: baseLine.sys.id || '',
      testData: baseLine.testData,
      baseUrl: baseLine.baseUrl,
      redirectUrl: baseLine.redirectUrl,
      ...baseLine,
    };
  }, [baseLine]);

  /**
   * Sample
   * @see https://github.com/ninetailed-inc/graphql-example/blob/main/lib/helpers.js
   */
  const parsed = useMemo(() => {
    try {
      const mappedExperiences = (
        (baseLine?.ntExperiencesCollection?.items ||
          []) as RequiredGetAbTestByIdQuery['experimentConfig']['ntExperiencesCollection']['items']
      )
        .filter(omitNullableHandler)
        .map((experience) => {
          return {
            id: experience.sys.id || '',
            name: experience.ntName || '',
            type: experience.ntType || '',
            config: experience.ntConfig,
            // Audience is optional in Ninetailed: without it the experience targets everyone.
            audience: experience.ntAudience?.ntAudienceId
              ? { id: experience.ntAudience.ntAudienceId }
              : undefined,
            variants: experience.ntVariantsCollection.items
              .filter((variant) => variant.__typename === 'ExperimentConfig')
              .map((variant) => {
                if (variant.__typename === 'ExperimentConfig') {
                  return {
                    id: variant?.sys.id || '',
                    ...variant,
                  };
                }
              })
              .filter(omitNullableHandler),
          };
        })
        .filter((experience) =>
          ExperienceMapper.isExperienceEntry<{
            id: string;
          }>(experience)
        )
        .map((experience) => ExperienceMapper.mapExperience(experience));

      return mappedExperiences;
    } catch (e) {
      console.warn(baseEntryId, e);
      return [];
    }
  }, [baseEntryId, baseLine?.ntExperiencesCollection?.items]);

  const { audience, experience, loading, status, variant, variantIndex } = useExperience({
    baseline: _baseline,
    experiences: parsed,
  });

  useEffect(() => {
    if (isFetched && !loading && status === 'success') {
      if (variant && !isVariantRef(variant)) {
        setExVariant(variantIndex);
      } else {
        setExVariant(variantIndex);
      }
    } else if (status === 'error') {
      setExVariant(0);
      console.warn('error on Ninetailed API');
    }
  }, [isFetched, loading, status, variant, variantIndex]);

  useEffect(() => {
    if (!loading && status === 'success' && variant && !isVariantRef(variant)) {
      try {
        setTestData(variant.testData);
      } catch (error) {
        setTestData(undefined);
      }
    }
  }, [loading, status, variant]);

  const [experienceId, setExperienceId] = useState<undefined | string>(undefined);

  useEffect(() => {
    setExperienceId(experience?.id);
  }, [experience]);

  useEffect(() => {
    if (shouldAddToHeader && exVariant !== undefined && experienceId) {
      setToHeader({
        experienceId,
        variant: exVariant,
      });
    }
  }, [exVariant, experienceId, setToHeader, shouldAddToHeader]);

  // ! This is for testing purpose. It needs to be removed after BE is ready
  useEffect(() => {
    setToHeader({
      experienceId: '5DFTkfeIFDQeE9pyEe0rs3',
      variant: 3,
    });
  }, [setToHeader]);

  const preExperienceId = usePrevious(experienceId);

  /**
   * Remove header once user is not eligible to experience anymore.
   * e.g Assigned NC logins and becomes EC.
   */
  useEffect(() => {
    if (preExperienceId && preExperienceId !== experienceId && !experienceId) {
      deleteHeader({ experienceId: preExperienceId });
    }
  }, [deleteHeader, experienceId, preExperienceId]);

  const { trackComponentView, observeElement, unobserveElement, onProfileChange } = useNinetailed();

  useEffect(() => {
    let detouchListeners: any = null;
    if (onExperimentChange) {
      detouchListeners = onProfileChange((pro) => {
        if (preExperienceId !== experienceId) {
          onExperimentChange(pro);
        }
      });
    }

    return () => {
      detouchListeners && detouchListeners();
    };
  }, [onExperimentChange, experienceId, onProfileChange, preExperienceId]);

  const { trackedExperiences, addTrackedExperience } = useExperienceTrackingContext();

  const trackComponentViewFn = useCallback(() => {
    if (
      elementRef ||
      !isClient ||
      !experience ||
      // We don't want to track personalization experiences
      experience?.type === 'nt_personalization'
    )
      return;

    // pass down fake dom to suppress error
    const payload = {
      element: document.createElement('div'),
      experience,
      audience,
      variant,
      variantIndex,
    };

    const trackingKey = `${experience.id}.${variantIndex}`;
    // We track the Experience view only if it has not been tracked yet
    if (!elementRef && !trackedExperiences.has(trackingKey)) {
      trackComponentView(payload);
      addTrackedExperience(trackingKey);
    }
  }, [
    elementRef,
    audience,
    experience,
    isClient,
    trackedExperiences,
    variant,
    variantIndex,
    addTrackedExperience,
    trackComponentView,
  ]);

  trackComponentViewFn();

  /**
   * Set up tracking effect to fires event to google analytics plugins
   */
  useEffect(() => {
    const componentElement = elementRef?.current;

    if (experience?.type === 'nt_personalization') return;

    if (componentElement) {
      observeElement({
        element: componentElement,
        experience,
        audience,
        variant: variant as any,
        variantIndex,
      });
    }

    return () => {
      if (componentElement) {
        unobserveElement(componentElement);
      }
    };
  }, [audience, elementRef, experience, observeElement, unobserveElement, variant, variantIndex]);

  const baseUrl = useMemo(() => {
    return !isVariantRef(variant) && variant ? variant.baseUrl : undefined;
  }, [variant]);

  const redirectUrl = useMemo(() => {
    return !isVariantRef(variant) && variant ? variant.redirectUrl : undefined;
  }, [variant]);

  useEffect(() => {
    if (exVariant !== undefined) {
      setAbTestCacheStore((prev) => {
        return {
          ...prev,
          [baseEntryId]: {
            ...prev[baseEntryId],
            variant: exVariant,
            testData,
            experienceId: `${experienceId}:${exVariant}`,
          },
        };
      });
    }
  }, [baseEntryId, exVariant, experienceId, setAbTestCacheStore, setExperienceId, testData]);

  return {
    variant: exVariant,
    loading: (loading || isLoading) && exVariant === undefined,
    testData,
    baseUrl,
    experienceId: experienceId && exVariant ? `${experienceId}:${exVariant}` : undefined,
    experienceName: experience?.name,
    redirectUrl,
    status,
    trackComponentViewFn,
  };
};

export default useExperiment;
